"use client";

import React, { useState, useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "../../atoms/Button";
import CheckIcon from "../../../assets/icons/done_24pxgreen.svg";
import ErrorIcon from "../../../assets/icons/info-error_24px_outlined.svg";
import { GlobalContext } from "@/contexts/SiteDataContext";
import sanitizeHTML from "@/utils/sanitizeHtml";
import { postContactForm } from "@/interface/api/PostContactForm/index";
import { fieldComponents } from "@/utils/Form";
import Wrapper from "@/components/organisms/Wrapper";
import Alert from "@/components/atoms/Alert";
import * as log from "@/actions/logger";
import "./styles.scss";

type FormProps = {
  data: any;
};

type FormState = "start" | "successForm";

const ContactForm: React.FC<FormProps> = ({ data }) => {
  const [formError, setFormError] = useState<boolean>(false);
  const [fields, setFields] = useState(data);
  const [formState, setFormState] = useState<FormState>("start");
  const { t, siteDataComplete, siteData } = useContext(GlobalContext).context;

  const contactShowInfo = {
    web_full_name: siteDataComplete.web_full_name,
    footer_name_company_1: siteDataComplete.footer_name_company_1,
    footer_direction: siteDataComplete.footer_direction,
    site_phone: siteDataComplete.site_phone,
  };

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, touchedFields, isSubmitted },
    getValues,
    reset,
    setError,
    clearErrors,
    watch,
    setValue,
    setFocus,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      requestTypes: "",
      name: "",
      email: "",
      applicationReference: "",
      message: "",
      privacyPolicy: "",
    },
  });

  const values = getValues();

  useEffect(() => {
    // This remove the "root" generic error from errors array to avoid ts error
    const fieldKeys = Object.keys(errors).filter(
      (key): key is Exclude<keyof typeof errors, "root"> => key !== "root"
    );

    const firstError = fieldKeys[0];
    if (firstError) setFocus(firstError);
  }, [errors, setFocus]);

  useEffect(() => {
    const updatedFields = fields.map((field: any) => {
      if (field.validation?.pattern?.value) {
        field.validation = {
          ...field.validation,
          pattern: {
            ...field.validation.pattern,
            value: new RegExp(field.validation.pattern.value),
          },
        };
      }
      return field;
    });

    setFields(updatedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data: any) => {
    try {
      const domain = window.location.origin;
      const response = await postContactForm(data, domain);
      // TODO: Move the log to back-end
      log.info({ res: response, module: "ContactForm" }, "Form submitted successfully");
      setFormState("successForm");
      window.scroll(0, 0);
    } catch (error) {
      // TODO: Move the log to back-end
      log.error({ err: error, module: "ContactForm" }, "Error submitting form:");
      setFormError(true);
    }
  };

  const startForm = () => {
    setFormState("start");
    reset();
  };

  const showSpecialNMIInfo = siteData.metadata.showSpecialNMIInfo ?? false;

  const start = (
    <div className="contact__wrapper u-box">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="doc">
        <div className="doc-container">
          {/* // TODO this is new */}
          {formError && (
            <Alert
              className="u-margin-bottom"
              type="error"
              svgIcon={<ErrorIcon />}
              content={t["We're sorry, an error has ocurred on submitting form. Please try again."]}
            />
          )}
          <div className="doc-step">
            {showSpecialNMIInfo && (
              <div className="contact__info">
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(contactShowInfo.footer_name_company_1),
                  }}
                />
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(contactShowInfo.footer_direction),
                  }}
                />
                <span
                  dangerouslySetInnerHTML={{
                    __html: "+1 (786) 2091728",
                  }}
                />
              </div>
            )}
            <p>
              {
                t[
                  "Use the form below to submit a support request. A customer service agent will be in touch with you as soon as possible. If submitted after office hours, we will respond on the next business day."
                ]
              }
            </p>
            <div className="doc-group m-contact-doc u-grid">
              {fields.map((field: any, index: number) => {
                const FieldComponent =
                  fieldComponents[field.component] || (() => <p>Tipo de campo no soportado</p>);

                return (
                  <FieldComponent
                    className={`u-grid-col-${field.cols ? field.cols : 12}`}
                    key={`${field.name}-${index}`}
                    t={t}
                    field={field}
                    register={register}
                    touchedFields={touchedFields}
                    isSubmitted={isSubmitted}
                    watch={watch}
                    values={values}
                    trigger={trigger}
                    setValue={setValue}
                    errors={errors}
                    setError={setError}
                    clearErrors={clearErrors}
                    component={field.component}
                  />
                );
              })}
            </div>
          </div>
          <div className="contact__submit">
            <Button
              primary
              data-qa="submit-form"
              label={t["Send Message"]}
              buttonSubmit
            />
          </div>
        </div>
      </form>
      <div className="contact__email e-text-helper">
        {t["Email address: info@"]}
        {siteData.domain}.
      </div>
    </div>
  );

  const successForm = (
    <Wrapper>
      <div className="contact__success">
        <CheckIcon title={t["Your message has been sent"]} />
        <p className="successMessage">{t["Your message has been sent"]}</p>
        <Button
          primary
          data-qa="submit-form"
          label={t["Send new message"]}
          onClick={startForm}
        />
      </div>
    </Wrapper>
  );

  const FormPhase: Record<FormState, JSX.Element> = {
    start,
    successForm,
  };

  return <div>{FormPhase[formState]}</div>;
};

export default ContactForm;
